import {PayloadAction} from '@reduxjs/toolkit'
import api from '../../agent'
import create from './base'
import {formatGameMinute} from "../../utils";

interface FixtureGameItem {
    id: number
    home: string
    away: string
    time: string
    live: boolean
    status: {
        id: number
        type: string
        code: number
        description: string
    }
    score: {
        home: number
        away: number
    } | null
    minute: number | null
    round: number | null
}

interface FixtureItem {
    id: number
    league: string
    competition: string
    type: string
    expanded: boolean
    games: FixtureGameItem[]
}

export interface FixturesState {
    activeTab: string,
    activeTipsTab: string,
    competitions: FixtureItem[],
    activeGame: any,
    headToHead: [],
    headToHeadFixtureId: string, // not sure how to return number from backend
    lastGamesHomeTeam: [],
    lastGamesAwayTeam: [],
    h2hLoading: boolean,
    momentum: [],
    odds: [],
    topFixtures: []
}

function transformApiResponse(events, state) {
    let fixtures = []

    // iterate over fixtures and add them to the tournaments array
    // if a tournament already exists, add the fixture to the games array
    // if it doesn't exist, create a new tournament object
    for (const fixture of events) {
        if (fixture.settings.isEditor) {
            continue
        }

        let tournament = fixtures.find((t: any) => t.id === fixture.tournament.id)
        if (tournament) {
            tournament.games.push(fixture)
        } else {
            fixtures.push({
                ...fixture.tournament,
                games: [fixture],
                expanded: state.competitions?.find((el) => el.id === fixture.tournament.id)?.expanded || false
            })
        }
    }

    // save new live key on game object if the status type is inprogress
    fixtures = fixtures
        .map((el: any) => ({
            ...el,
            games: el.games
                .map((game: any) => ({
                    ...game,
                    live: game.status.type === 'inprogress'
                }))
                .sort((a: any, b: any) => (b.homeTeam.userCount + b.awayTeam.userCount) - (a.homeTeam.userCount + a.awayTeam.userCount))
                .sort((a: any, b: any) => a.startTimestamp - b.startTimestamp)
        }))
        .sort((a: any, b: any) => b.priority - a.priority)

    const hasExpandedCompetition = fixtures.find((c: any) => c.expanded)
    if (!hasExpandedCompetition) {
        fixtures = fixtures
            .map((el: any, index: any) => ({
                ...el,
                expanded: index < 10 || el.games.find((g: any) => g.live) && el.games.filter((g: any) => g.live).length < 10
            }))
    }

    return fixtures
}

function calculatePressure(data) {
    // Extract statistics for both teams
    const homeStats = parseFloat(data.statistics.find(stat => stat.eventPeriod === 'ALL' && stat.groupName === 'Match overview' && stat.name === 'Ball possession')?.homeValue || 0);
    const awayStats = parseFloat(data.statistics.find(stat => stat.eventPeriod === 'ALL' && stat.groupName === 'Match overview' && stat.name === 'Ball possession')?.awayValue || 0);

    const homeShots = parseFloat(data.statistics.find(stat => stat.eventPeriod === 'ALL' && stat.groupName === 'Shots' && stat.name === 'Total shots')?.homeValue || 0);
    const awayShots = parseFloat(data.statistics.find(stat => stat.eventPeriod === 'ALL' && stat.groupName === 'Shots' && stat.name === 'Total shots')?.awayValue || 0);

    const homeShotsOnTarget = parseFloat(data.statistics.find(stat => stat.eventPeriod === 'ALL' && stat.groupName === 'Shots' && stat.name === 'Shots on target')?.homeValue || 0);
    const awayShotsOnTarget = parseFloat(data.statistics.find(stat => stat.eventPeriod === 'ALL' && stat.groupName === 'Shots' && stat.name === 'Shots on target')?.awayValue || 0);

    const homeBigChances = parseFloat(data.statistics.find(stat => stat.eventPeriod === 'ALL' && stat.groupName === 'Match overview' && stat.name === 'Big chances')?.homeValue || 0) ;
    const awayBigChances = parseFloat(data.statistics.find(stat => stat.eventPeriod === 'ALL' && stat.groupName === 'Match overview' && stat.name === 'Big chances')?.awayValue || 0) ;

    // Define a weight for each metric
    const weights = {
        possession: 0.1,
        shots: 0.3,
        shotsOnTarget: 0.5,
        bigChances: 0.3
    };

    // Calculate normalized values for each statistic
    const maxPossession = 100; // Maximum ball possession percentage
    const maxShots = Math.max(homeShots, awayShots) || 1; // Avoid division by zero
    const maxShotsOnTarget = Math.max(homeShotsOnTarget, awayShotsOnTarget) || 1; // Avoid division by zero
    const maxBigChances = Math.max(homeBigChances, awayBigChances) || 1; // Avoid division by zero

    // Calculate pressure for home and away teams
    const homePressure = (homeStats / maxPossession * weights.possession) +
        (homeShots / maxShots * weights.shots) +
        (homeShotsOnTarget / maxShotsOnTarget * weights.shotsOnTarget) +
        (homeBigChances / maxBigChances * weights.bigChances);

    const awayPressure = (awayStats / maxPossession * weights.possession) +
        (awayShots / maxShots * weights.shots) +
        (awayShotsOnTarget / maxShotsOnTarget * weights.shotsOnTarget) +
        (awayBigChances / maxBigChances * weights.bigChances);

    // Calculate total pressure
    const totalPressure = homePressure + awayPressure;

    // Calculate pressure percentages
    const homePressurePercentage = Math.round(homePressure / totalPressure * 100);
    const awayPressurePercentage = Math.round(awayPressure / totalPressure * 100);

    return {
        homePressure: isNaN(homePressurePercentage) ? 50 : homePressurePercentage,
        awayPressure: isNaN(awayPressurePercentage) ? 50 : awayPressurePercentage,
    };
}


const FixturesRedux = create('fixtures', {
    activeTab: 'all',
    activeTipsTab: 'live',
    competitions: [],
    activeGame: null,
    headToHeadFixtureId: null,
    headToHead: [],
    lastGamesHomeTeam: [],
    lastGamesAwayTeam: [],
    h2hLoading: false,
    momentum: [],
    odds: [],
    topFixtures: []
}, {
    enable: (state: FixturesState, action: PayloadAction<number>) => {

    },
    changeActiveTab: (state: FixturesState, action: PayloadAction<number>) => ({
        ...state,
        activeTab: action.payload
    }),
    changeActiveTipsTab: (state: FixturesState, action: PayloadAction<number>) => ({
        ...state,
        activeTipsTab: action.payload
    }),
    expandCompetition: (state: FixturesState, action: PayloadAction<number>) => {
        const updatedCompetitions = state.competitions.map((competition: FixtureItem) => {
            if (competition.id === action.payload) {
                return {
                    ...competition,
                    expanded: !competition.expanded
                };
            }
            return competition;
        });

        return {
            ...state,
            competitions: updatedCompetitions
        };
    }
}, {
    getFixtures: async (state: FixturesState, params: any = {}) => {
        const response = await api.getFixtures()

        return {
            competitions: transformApiResponse(response.data, state)//.slice(0, 20)
        }
    },
    details: async (state: FixturesState, event_id: any) => {
        let response = await api.getFixtureDetails(event_id)
        let pressure = null

        // if (response.data.status.type !== 'notstarted') {
        //     try {
        //         pressure = calculatePressure(response.data);
        //     } catch (e) {
        //         console.log(e)
        //     }
        // }

        let newState =  {
            activeGame: {
                ...response.data,
                isLive: response.data.status.type === 'inprogress',
                pressure: pressure,
                // @ts-ignore
                analysis: state.topFixtures.find((el: any) => el.eventId === event_id)?.analysis?.picks || null
            }
        }

        if (state.competitions?.length) {
            // @ts-ignore
            newState.competitions = state.competitions.map((competition: FixtureItem) => {
                if (competition.games.find((game: any) => game.id === event_id) && !competition.expanded) {
                    return {
                        ...competition,
                        expanded: true
                    }
                }

                return competition
            })
        }

        return newState
    },
    disableActiveGame: async (state: FixturesState) => ({
        activeGame: null
    }),
    headToHeadFixtures: async (state: FixturesState, params: any = {}) => {
        const response = await api.getHeadToHeadFixtures(params)

        return {
            headToHeadFixtureId: response.data.fixtureId,
            headToHead: response.data.headToHead.filter((el) => el.status.type === 'finished'),
            lastGamesHomeTeam: response.data.homeTeam.filter((el) => el.status.type === 'finished'),
            lastGamesAwayTeam: response.data.awayTeam.filter((el) => el.status.type === 'finished'),
        }
    },
    lastGamesHomeTeam: async (state: FixturesState, params: any = {}) => {
        const response = await api.getLastGamesTeam(params)

        return {
            lastGamesHomeTeam: response.data.filter((el) => el.status.type === 'finished')
        }
    },
    lastGamesAwayTeam: async (state: FixturesState, params: any = {}) => {
        const response = await api.getLastGamesTeam(params)

        return {
            lastGamesAwayTeam: response.data.filter((el) => el.status.type === 'finished')
        }
    },
    getLiveOdds: async (state: FixturesState, params: any = {}) => {
        const response = await api.getOdds(params)

        if (!response.hasOwnProperty('data')) {
            return {
                odds: []
            }
        }

        const groupedData = response.data.reduce((acc, item) => {
            let key = item.marketName + item.live.toString();

            if (item.choiceGroup) {
                key += item.choiceGroup;
            }

            if (!acc[key]) {
                acc[key] = {
                    sourceId: item.source,
                    marketId: Object.keys(acc).length + 1,
                    marketName: item.marketName,
                    isLive: item.live,
                    fid: item.source,
                    suspended: item.status !== "ODDS_STATUS_ACTIVE",
                    id: item.id,
                    choiceGroup: item.choiceGroup,
                    choices: []
                };
            }

            acc[key].choices.push({
                initialFractionalValue: item.valueInitialFractional,
                fractionalValue: item.valueFractional,
                valueDecimal: item.valueDecimal,
                sourceId: item.id,
                name: item.choiceName,
                winning: item.choiceWinning,
                change: item.choiceChange
            });

            return acc;
        }, {});

        return {
            odds: Object.values(groupedData) || []
        }
    },
    getMomentum: async (state: FixturesState, params: any = {}) => {
        const response = await api.getMomentum(params)
        let momentum = response.data || []
        let gameTime = parseInt(formatGameMinute(state.activeGame), 10);

        if (state.activeGame.status.description === 'Ended') {
            gameTime = 90;
        }

        for (let i = 0; i < (gameTime > 90 ? gameTime : 90); i++) {
            if (!momentum.find((el) => el.minute === i.toString())) {
                momentum.push({
                    minute: i.toString(),
                    value: 0,
                })
            }
        }

        // Find the maximum absolute value
        const maxValue = Math.max(...momentum.map(el => Math.abs(el.value)));

        // Normalize the values to have the maximum value closer to 100
        const normalizationFactor = 100 / maxValue;
        const normalizedMomentum = momentum.map(el => {
            const goal = state.activeGame.incidents?.find((i: any) => i.incidentType === 'goal' && i.time === el.minute);
            const yellowCards = state.activeGame.incidents?.filter((i: any) => i.incidentType === 'card' && i.incidentClass === 'yellow' && i.time === el.minute);
            const yellowReds = state.activeGame.incidents?.filter((i: any) => i.incidentType === 'card' && i.incidentClass === 'yellowRed' && i.time === el.minute);
            const redCards = state.activeGame.incidents?.filter((i: any) => i.incidentType === 'card' && i.incidentClass === 'red' && i.time === el.minute);
            const substitutions = state.activeGame.incidents?.filter((i: any) => i.incidentType === 'substitution' && i.time === el.minute);
            // const corners = state.activeGame.comments?.filter((c: any) => c.type === 'cornerKick' && c.time == el.minute);
            const shotsOnTarget = state.activeGame.comments?.filter((c: any) => c.type === 'shotSaved' && c.time == el.minute);
            const shotsOffTarget = state.activeGame.comments?.filter((c: any) => c.type === 'shotOffTarget' && c.time == el.minute);

            return {
                ...el,
                value: el.value * normalizationFactor,
                goal: goal || null,
                yellowCards: yellowCards || null,
                yellowReds: yellowReds || null,
                redCards: redCards || null,
                substitutions: substitutions || null,
                // corners: corners || null,
                shotsOnTarget: shotsOnTarget || null,
                shotsOffTarget: shotsOffTarget || null,
            }
        });

        return {
            momentum: normalizedMomentum
        }
    },
    clearMomentum: async (state: FixturesState) => ({
        momentum: []
    }),
    h2hLoading: async (state: FixturesState, loading: boolean) => ({
        h2hLoading: loading
    }),
    getTopFixtures: async (state: FixturesState, params: any = {}) => {
        if (state.topFixtures.length > 0) {
            return {
                topFixtures: state.topFixtures
            }
        }

        const topFixtures = await api.getSorter()
        const promises = []

        for (const fixture of topFixtures.data) {
            promises.push(api.getFixtureDetails(fixture.eventId))
            // promises.push(api.getOdds(fixture.eventId))
        }

        let data = await Promise.all(promises)

        return {
            topFixtures: topFixtures.data.map((el: any) => ({
                ...el,
                ...data.find((d: any) => d.data.id === el.eventId).data,
                analysis: el
            })),
        }
    }
})

export const fetchAllGameData = (id: number) => async (dispatch: any) => {
    await dispatch(h2hLoading(true))
    await dispatch(headToHeadFixtures(id));
    await dispatch(h2hLoading(false))
};

export const {
    getFixtures,
    details,
    headToHeadFixtures,
    lastGamesHomeTeam,
    lastGamesAwayTeam,
    changeActiveTab,
    changeActiveTipsTab,
    expandCompetition,
    disableActiveGame,
    h2hLoading,
    getMomentum,
    getLiveOdds,
    clearMomentum,
    getTopFixtures
} = FixturesRedux.actions

export default FixturesRedux.reducer
